import React, { useCallback, FC, useMemo } from 'react';
import { Table } from 'src/common';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { useLoadWebhooks } from 'src/models/company/Webhooks/hooks';
import { IApiWebhook } from 'src/models/company/Webhooks/types';
import { ColumnProps } from 'antd/lib/table';
import { GetComponentProps } from 'rc-table/lib/interface';
import { WebhookDelete, WebhookUpdate } from '../WebhooksActions';

function useTableColumns(): ColumnProps<IApiWebhook>[] {
  const { t } = useTranslation();
  return useMemo(() => {
    return [
      {
        title: t('Common.name'),
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        className: styles.colName,
        render: (_, record) => <span>{record.name}</span>
      },
      {
        title: t('Common.url'),
        dataIndex: 'url',
        key: 'url',
        ellipsis: true,
        className: styles.colUrl,
        render: (_, record) => {
          return <span>{record.url}</span>;
        }
      },
      {
        title: t('Webhooks.eventType'),
        dataIndex: 'eventType',
        key: 'eventType',
        ellipsis: true,
        className: styles.colEventType,
        render: (_, record) => {
          return t(`Webhooks.event${record.eventType}`);
        }
      },
      {
        title: '',
        dataIndex: 'actions',
        key: 'actions',
        className: styles.colActions,
        onCell: () => ({
          onClick: (e: React.MouseEvent) => {
            e.stopPropagation();
          }
        }),
        render: (_, record) => {
          return (
            <ul className={styles.actions}>
              <li className={styles.action}>
                <WebhookUpdate webhook={record} />
              </li>
              <li className={styles.action}>
                <WebhookDelete id={record.id} />
              </li>
            </ul>
          );
        }
      }
    ];
  }, [t]);
}

interface IProps {}

export const WebhooksList: FC<IProps> = () => {
  const { data: webhooksData, loading: webhooksLoading } = useLoadWebhooks();
  const webhooks = webhooksData?.getWebhooks;

  const data = useMemo(() => {
    return (
      (webhooks &&
        webhooks.map(
          (webhook): IApiWebhook => ({
            id: webhook.id,
            name: webhook.name,
            url: webhook.url,
            eventType: webhook.eventType,
            secret: webhook.secret
          })
        )) ||
      []
    );
  }, [webhooks]);

  const columns: ColumnProps<IApiWebhook>[] = useTableColumns();
  const onRow: GetComponentProps<IApiWebhook> = useCallback(() => {
    return {
      onClick: e => {
        e.stopPropagation();
      }
    };
  }, []);

  return (
    <div className={styles.webhooksList}>
      {webhooks && webhooks?.length !== 0 && (
        <Table
          loading={webhooksLoading}
          columns={columns}
          dataSource={data}
          onRow={onRow}
          rowKey={'id'}
          rowClassName={styles.row}
          className={styles.table}
          size={'large'}
        />
      )}
    </div>
  );
};

export default WebhooksList;
